/* Vannucci : boutons « + Ajouter » des suggestions du panier (vac-cart-upsell).
   Même circuit que le bouton d'achat du thème : le panier latéral et la page panier se mettent à jour sans rechargement.
   Après la réponse de Shopify, le bouton émet « vcu:done » (detail.ok = true / false) : les cartes produit
   s'en servent pour afficher « Ajouté » seulement quand l'ajout est réellement confirmé. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADD_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js';
const CART_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart.js';

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function addToCart(button) {
  const variantId = button.getAttribute('data-vcu-add');
  // Quantité facultative (sélecteur de flacons de la fiche produit), 1 par défaut
  const quantity = Math.min(10, Math.max(1, parseInt(button.getAttribute('data-vcu-qty') || '1', 10) || 1));
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;
  const box = button.closest('[data-vcu]');
  const error = box && box.querySelector('[data-vcu-error]');
  if (error) error.hidden = true;
  button.setAttribute('aria-busy', 'true');

  let ok = false;
  let message = '';
  let shopifyDescription = '';
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: ids.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    const cart = await fetch(CART_URL, { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());

    if (!response.ok || data.status) {
      button.dispatchEvent(
        new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'vac-cart-upsell', sourceId: 'vac-cart-upsell', itemCount: quantity },
      });
      shopifyDescription = typeof data.description === 'string' ? data.description : '';
      throw new Error('cart-add');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'vac-cart-upsell',
        sourceId: 'vac-cart-upsell',
        itemCount: quantity,
        sections: data.sections,
      },
    });
    ok = true;
  } catch (e) {
    if (deferred.reject) deferred.reject(e);
    // Seule la description renvoyée par Shopify (en français) est montrée ; jamais un message technique du navigateur
    message = (shopifyDescription ? shopifyDescription + ' ' : '') + 'Ce produit n’a pas pu être ajouté. Veuillez réessayer.';
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  } finally {
    button.removeAttribute('aria-busy');
    button.dispatchEvent(new CustomEvent('vcu:done', { bubbles: true, detail: { ok, message } }));
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-vcu-add]') : null;
  if (!button) return;
  event.preventDefault();
  addToCart(button);
});

/* ---------- Retour visuel des cartes produit (snippet vac-carte-achat) ---------- */
// Zone d'annonce créée dès le chargement : une zone live insérée en même temps que son texte n'est souvent pas lue.
const statut = (() => {
  let el = document.getElementById('vcu-statut');
  if (!el) {
    el = document.createElement('p');
    el.id = 'vcu-statut';
    el.className = 'visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  return el;
})();
let statutTimer = null;

function annoncer(texte) {
  // On vide puis on réécrit : un 2e ajout du même produit (phrase identique) est relu
  clearTimeout(statutTimer);
  statut.textContent = '';
  if (texte) statutTimer = setTimeout(() => { statut.textContent = texte; }, 100);
}

document.addEventListener('vcu:done', (event) => {
  const btn = event.target;
  if (!(btn instanceof HTMLElement) || !btn.classList.contains('vcp-card__cta')) return;
  const label = btn.querySelector('.vcp-card__cta-label');
  if (!btn.dataset.label) btn.dataset.label = (label && label.textContent.trim()) || 'Ajouter';
  const libelle = btn.dataset.label;
  const remettre = () => {
    btn.classList.remove('vcp-card__cta--ok');
    if (label) label.textContent = libelle;
  };
  clearTimeout(btn._vcpTimer);
  if (!event.detail || !event.detail.ok) {
    // Échec (ex. stock atteint au 2e clic) : plus de « Ajouté » ; le message d'erreur s'affiche sous le bouton
    remettre();
    annoncer('');
    return;
  }
  btn.classList.add('vcp-card__cta--ok');
  if (label) label.textContent = 'Ajouté';
  annoncer('Ajouté au panier : ' + (btn.dataset.vcpTitre || 'produit'));
  // Si un filtre ou un tri a remplacé la carte entre-temps, le bouton n'est plus dans la page : rien à remettre
  btn._vcpTimer = setTimeout(() => { if (btn.isConnected) remettre(); }, 2000);
});

/* ---------- Petite recherche « Compléter mon panier » (recherche prédictive Shopify) ---------- */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
const priceFormat = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency });
let searchTimer = null;
let searchController = null;

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function resultRow(product) {
  const variants = (product.variants || []).filter((v) => v.available);
  const single = (product.variants || []).length === 1 && variants.length === 1;
  const price = priceFormat.format(single ? variants[0].price / 100 : Number(product.price) || 0);
  const image = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
  const thumb = image
    ? `<img src="${escapeHtml(image)}${String(image).includes('?') ? '&' : '?'}width=120" alt="" width="40" height="50" loading="lazy">`
    : '';
  const action = single
    ? `<button type="button" class="vcu__add" data-vcu-add="${variants[0].id}" aria-label="Ajouter ${escapeHtml(product.title)} au panier">+ Ajouter</button>`
    : `<a class="vcu__add" href="${escapeHtml(product.url)}" aria-label="Choisir une option de ${escapeHtml(product.title)}">Choisir</a>`;
  return `<li class="vcs__item"><span class="vcs__thumb">${thumb}</span><span class="vcs__info"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a><span>${escapeHtml(price)}</span></span>${action}</li>`;
}

async function runSearch(box, query) {
  const results = box.querySelector('[data-vcs-results]');
  const status = box.querySelector('[data-vcs-status]');
  if (query.length < 2) {
    results.hidden = true;
    results.innerHTML = '';
    if (status) status.textContent = '';
    return;
  }
  if (searchController) searchController.abort();
  searchController = new AbortController();
  const params = new URLSearchParams({
    q: query,
    'resources[type]': 'product',
    'resources[limit]': '5',
    'resources[options][unavailable_products]': 'hide',
  });
  try {
    const response = await fetch(`${ROOT}search/suggest.json?${params}`, { signal: searchController.signal });
    const data = await response.json();
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    // La recherche prédictive ne renvoie pas les variantes : on lit chaque fiche (.js) pour savoir si l'ajout direct est possible
    await Promise.all(
      products.map((product) =>
        fetch(`${ROOT}products/${encodeURIComponent(product.handle)}.js`, { signal: searchController.signal })
          .then((r) => (r.ok ? r.json() : null))
          .then((full) => { if (full && full.variants) product.variants = full.variants; })
          .catch(() => {})
      )
    );
    results.innerHTML = products.length
      ? products.map(resultRow).join('')
      : '<li class="vcs__empty">Aucun produit trouvé.</li>';
    results.hidden = false;
    if (status) status.textContent = products.length ? `${products.length} produit${products.length > 1 ? 's' : ''} trouvé${products.length > 1 ? 's' : ''}` : 'Aucun produit trouvé';
  } catch (e) {
    if (e && e.name === 'AbortError') return;
    results.hidden = true;
  }
}

document.addEventListener('input', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-vcs-input]') : null;
  if (!input) return;
  const box = input.closest('[data-vcs]');
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => runSearch(box, input.value.trim()), 250);
});

document.addEventListener('keydown', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-vcs-input]') : null;
  if (!input) return;
  if (event.key === 'Escape' && input.value) {
    event.preventDefault();
    event.stopPropagation();
    input.value = '';
    runSearch(input.closest('[data-vcs]'), '');
  }
  if (event.key === 'Enter') event.preventDefault();
});
